/**
 * useInboxState — core state hook for the inbox email client.
 *
 * Wraps mock email data (Phase 1) with folder filtering, read/unread tracking,
 * star toggling, and queue management. All state persisted in localStorage.
 */
import { useState, useCallback, useMemo } from 'react';
import { MOCK_EMAILS } from './mock/mockEmails';

const STORAGE_KEY = 'nexonurse_inbox_state';

function loadPersistedState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function persistState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      readIds: [...state.readIds],
      starredIds: [...state.starredIds],
      folders: state.folders,
      queueStatuses: state.queueStatuses,
      boardSwimlanes: state.boardSwimlanes,
      scheduledDates: state.scheduledDates,
      reclassifiedDomains: state.reclassifiedDomains,
    }));
  } catch { /* localStorage full — degrade silently */ }
}

function initState() {
  const saved = loadPersistedState();
  const readIds = new Set(saved?.readIds ?? MOCK_EMAILS.filter((e) => e.isRead).map((e) => e.id));
  const starredIds = new Set(saved?.starredIds ?? MOCK_EMAILS.filter((e) => e.isStarred).map((e) => e.id));
  const folders = saved?.folders ?? Object.fromEntries(MOCK_EMAILS.map((e) => [e.id, e.folder]));
  const queueStatuses = saved?.queueStatuses ?? Object.fromEntries(MOCK_EMAILS.map((e) => [e.id, e.queueStatus]));
  const boardSwimlanes = saved?.boardSwimlanes ?? Object.fromEntries(MOCK_EMAILS.map((e) => [e.id, e.boardSwimlane]));
  const scheduledDates = saved?.scheduledDates ?? Object.fromEntries(MOCK_EMAILS.map((e) => [e.id, e.scheduledDate]));
  const reclassifiedDomains = saved?.reclassifiedDomains ?? {};
  return { readIds, starredIds, folders, queueStatuses, boardSwimlanes, scheduledDates, reclassifiedDomains };
}

export function useInboxState() {
  const [state, setState] = useState(initState);

  const update = useCallback((updater) => {
    setState((prev) => {
      const next = updater(prev);
      persistState(next);
      return next;
    });
  }, []);

  // Derive enriched email list
  const emails = useMemo(() =>
    MOCK_EMAILS.map((e) => ({
      ...e,
      isRead: state.readIds.has(e.id),
      isStarred: state.starredIds.has(e.id),
      folder: state.folders[e.id] ?? e.folder,
      queueStatus: state.queueStatuses[e.id] ?? e.queueStatus,
      boardSwimlane: state.boardSwimlanes[e.id] ?? e.boardSwimlane,
      scheduledDate: state.scheduledDates[e.id] ?? e.scheduledDate,
      primaryDomain: state.reclassifiedDomains[e.id] ?? e.primaryDomain,
    })),
    [state]
  );

  // ── Actions ───────────────────────────────────────────────────────────────

  const markRead = useCallback((id) => {
    update((s) => {
      const next = new Set(s.readIds);
      next.add(id);
      return { ...s, readIds: next };
    });
  }, [update]);

  const markUnread = useCallback((id) => {
    update((s) => {
      const next = new Set(s.readIds);
      next.delete(id);
      return { ...s, readIds: next };
    });
  }, [update]);

  const toggleStar = useCallback((id) => {
    update((s) => {
      const next = new Set(s.starredIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...s, starredIds: next };
    });
  }, [update]);

  const moveToFolder = useCallback((id, folder) => {
    update((s) => ({ ...s, folders: { ...s.folders, [id]: folder } }));
  }, [update]);

  const archiveEmail = useCallback((id) => moveToFolder(id, 'archive'), [moveToFolder]);
  const trashEmail = useCallback((id) => moveToFolder(id, 'trash'), [moveToFolder]);

  const queueEmail = useCallback((id) => {
    update((s) => ({
      ...s,
      queueStatuses: { ...s.queueStatuses, [id]: 'queued' },
      boardSwimlanes: { ...s.boardSwimlanes, [id]: 'today' },
    }));
  }, [update]);

  const scheduleEmail = useCallback((id, date) => {
    update((s) => ({
      ...s,
      queueStatuses: { ...s.queueStatuses, [id]: 'scheduled' },
      scheduledDates: { ...s.scheduledDates, [id]: date },
      boardSwimlanes: { ...s.boardSwimlanes, [id]: 'scheduled' },
    }));
  }, [update]);

  const boardEmail = useCallback((id, swimlane) => {
    update((s) => ({
      ...s,
      queueStatuses: { ...s.queueStatuses, [id]: 'queued' },
      boardSwimlanes: { ...s.boardSwimlanes, [id]: swimlane },
      // Clear scheduled date when moving away from scheduled column
      scheduledDates: { ...s.scheduledDates, [id]: swimlane === 'scheduled' ? s.scheduledDates[id] : null },
    }));
  }, [update]);

  const dismissEmail = useCallback((id) => {
    update((s) => ({
      ...s,
      folders: { ...s.folders, [id]: 'archive' },
      queueStatuses: { ...s.queueStatuses, [id]: 'none' },
    }));
  }, [update]);

  const completeEmail = useCallback((id) => {
    update((s) => ({
      ...s,
      queueStatuses: { ...s.queueStatuses, [id]: 'completed' },
      boardSwimlanes: { ...s.boardSwimlanes, [id]: null },
    }));
  }, [update]);

  const reclassify = useCallback((id, domain) => {
    update((s) => ({
      ...s,
      reclassifiedDomains: { ...s.reclassifiedDomains, [id]: domain },
    }));
  }, [update]);

  // ── Thread-level actions (apply to all emails sharing a threadId) ─────────

  /** Resolve all email IDs that share a threadId with the given email */
  const getThreadIds = useCallback((id) => {
    const email = MOCK_EMAILS.find((e) => e.id === id);
    if (!email?.threadId) return [id];
    return MOCK_EMAILS.filter((e) => e.threadId === email.threadId).map((e) => e.id);
  }, []);

  const queueThread = useCallback((id) => {
    const ids = getThreadIds(id);
    update((s) => {
      const qs = { ...s.queueStatuses };
      const bs = { ...s.boardSwimlanes };
      ids.forEach((eid) => { qs[eid] = 'queued'; bs[eid] = 'today'; });
      return { ...s, queueStatuses: qs, boardSwimlanes: bs };
    });
  }, [update, getThreadIds]);

  const scheduleThread = useCallback((id, date) => {
    const ids = getThreadIds(id);
    update((s) => {
      const qs = { ...s.queueStatuses };
      const sd = { ...s.scheduledDates };
      const bs = { ...s.boardSwimlanes };
      ids.forEach((eid) => { qs[eid] = 'scheduled'; sd[eid] = date; bs[eid] = 'scheduled'; });
      return { ...s, queueStatuses: qs, scheduledDates: sd, boardSwimlanes: bs };
    });
  }, [update, getThreadIds]);

  const boardThread = useCallback((id, swimlane) => {
    const ids = getThreadIds(id);
    update((s) => {
      const qs = { ...s.queueStatuses };
      const bs = { ...s.boardSwimlanes };
      const sd = { ...s.scheduledDates };
      ids.forEach((eid) => {
        qs[eid] = 'queued';
        bs[eid] = swimlane;
        if (swimlane !== 'scheduled') sd[eid] = null;
      });
      return { ...s, queueStatuses: qs, boardSwimlanes: bs, scheduledDates: sd };
    });
  }, [update, getThreadIds]);

  const dismissThread = useCallback((id) => {
    const ids = getThreadIds(id);
    update((s) => {
      const f = { ...s.folders };
      const qs = { ...s.queueStatuses };
      ids.forEach((eid) => { f[eid] = 'archive'; qs[eid] = 'none'; });
      return { ...s, folders: f, queueStatuses: qs };
    });
  }, [update, getThreadIds]);

  // ── Bulk actions ──────────────────────────────────────────────────────────

  const bulkMarkRead = useCallback((ids) => {
    update((s) => {
      const next = new Set(s.readIds);
      ids.forEach((id) => next.add(id));
      return { ...s, readIds: next };
    });
  }, [update]);

  const bulkArchive = useCallback((ids) => {
    update((s) => {
      const nextFolders = { ...s.folders };
      ids.forEach((id) => { nextFolders[id] = 'archive'; });
      return { ...s, folders: nextFolders };
    });
  }, [update]);

  const bulkTrash = useCallback((ids) => {
    update((s) => {
      const nextFolders = { ...s.folders };
      ids.forEach((id) => { nextFolders[id] = 'trash'; });
      return { ...s, folders: nextFolders };
    });
  }, [update]);

  const bulkMoveToInbox = useCallback((ids) => {
    update((s) => {
      const nextFolders = { ...s.folders };
      ids.forEach((id) => { nextFolders[id] = 'inbox'; });
      return { ...s, folders: nextFolders };
    });
  }, [update]);

  const bulkMarkUnread = useCallback((ids) => {
    update((s) => {
      const next = new Set(s.readIds);
      ids.forEach((id) => next.delete(id));
      return { ...s, readIds: next };
    });
  }, [update]);

  const resetState = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setState(initState());
  }, []);

  // ── Folder counts ─────────────────────────────────────────────────────────

  const folderCounts = useMemo(() => {
    const counts = { inbox: 0, sent: 0, drafts: 0, archive: 0, trash: 0 };
    const unread = { inbox: 0, sent: 0, drafts: 0, archive: 0, trash: 0 };
    emails.forEach((e) => {
      const f = e.folder;
      // Triaged inbox emails don't count toward inbox totals
      if (f === 'inbox' && e.queueStatus !== 'none') return;
      if (counts[f] !== undefined) counts[f]++;
      if (!e.isRead && unread[f] !== undefined) unread[f]++;
    });
    return { total: counts, unread };
  }, [emails]);

  return {
    emails,
    folderCounts,
    actions: {
      markRead, markUnread, toggleStar,
      moveToFolder, archiveEmail, trashEmail,
      queueEmail, scheduleEmail, boardEmail, dismissEmail, completeEmail,
      queueThread, scheduleThread, boardThread, dismissThread,
      reclassify,
      bulkMarkRead, bulkMarkUnread, bulkArchive, bulkTrash, bulkMoveToInbox,
      resetState,
    },
  };
}
